import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import apiClient from '../../../utils/apiClient';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Card } from '../../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { toast } from 'sonner';
import { ArrowLeft, Database, HardDrive, Check, X, Loader2 } from 'lucide-react';

export default function InfrastructureSettings() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [dbConfig, setDbConfig] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [formData, setFormData] = useState({
    dbType: 'mongodb',
    host: '',
    port: 27017,
    database: '',
    username: '',
    password: '',
    ssl: false,
  });

  useEffect(() => {
    fetchConfig();
  }, [orgId]);

  const fetchConfig = async () => {
    try {
      const response = await apiClient.get(`/enterprise/orgs/${orgId}/infrastructure/database`);
      setDbConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch config:', error);
    }
  };

  const handleSaveConfig = async () => {
    try {
      await apiClient.post(`/enterprise/orgs/${orgId}/infrastructure/database`, formData);
      toast.success('Database configuration saved');
      fetchConfig();
    } catch (error) {
      toast.error('Failed to save configuration');
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await apiClient.post(`/enterprise/orgs/${orgId}/infrastructure/database/test`);
      setTestResult(response.data);
      if (response.data.status === 'success') {
        toast.success('Connection successful!');
      } else {
        toast.error(response.data.message);
      }
      fetchConfig();
    } catch (error) {
      setTestResult({ status: 'failed', message: 'Connection test failed' });
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/enterprise/orgs/${orgId}/settings`)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900">Infrastructure Settings</h1>
          <p className="text-gray-600 mt-1">Configure BYODB and storage options</p>
        </div>
      </div>

      <Tabs defaultValue="database">
        <TabsList>
          <TabsTrigger value="database">
            <Database className="w-4 h-4 mr-2" />
            Database (BYODB)
          </TabsTrigger>
          <TabsTrigger value="storage">
            <HardDrive className="w-4 h-4 mr-2" />
            Storage (BYOS)
          </TabsTrigger>
        </TabsList>

        <TabsContent value="database" className="space-y-4">
          {dbConfig?.configured && (
            <Card className="p-4 rounded-lg">
              <div className="flex items-center gap-3">
                {dbConfig.status === 'connected' ? (
                  <div className="p-2 bg-success/10 rounded-lg">
                    <Check className="w-5 h-5 text-success" />
                  </div>
                ) : dbConfig.status === 'failed' ? (
                  <div className="p-2 bg-error/10 rounded-lg">
                    <X className="w-5 h-5 text-error" />
                  </div>
                ) : (
                  <div className="p-2 bg-warning/10 rounded-lg">
                    <Database className="w-5 h-5 text-warning" />
                  </div>
                )}
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">
                    Database: {dbConfig.dbType?.toUpperCase()}
                  </p>
                  <p className="text-sm text-gray-600">
                    Status: <span className="capitalize">{dbConfig.status}</span>
                  </p>
                  {dbConfig.lastTested && (
                    <p className="text-xs text-gray-500">
                      Last tested: {new Date(dbConfig.lastTested).toLocaleString()}
                    </p>
                  )}
                </div>
                <Button
                  onClick={handleTestConnection}
                  disabled={testing}
                  size="sm"
                >
                  {testing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Testing...
                    </>
                  ) : (
                    'Test Connection'
                  )}
                </Button>
              </div>
            </Card>
          )}

          <Card className="p-6 rounded-lg space-y-4">
            <div>
              <h3 className="font-heading text-lg font-semibold text-gray-900 mb-2">
                Bring Your Own Database (BYODB)
              </h3>
              <p className="text-sm text-gray-600">
                Connect your own MongoDB or CosmosDB instance for data sovereignty
              </p>
            </div>

            <div>
              <Label>Database Type</Label>
              <select
                value={formData.dbType}
                onChange={(e) => setFormData({ ...formData, dbType: e.target.value })}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
              >
                <option value="mongodb">MongoDB</option>
                <option value="cosmosdb">Azure CosmosDB</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Host *</Label>
                <Input
                  value={formData.host}
                  onChange={(e) => setFormData({ ...formData, host: e.target.value })}
                  placeholder="localhost or cluster.mongodb.net"
                  className="mt-1 font-mono text-sm"
                />
              </div>
              <div>
                <Label>Port *</Label>
                <Input
                  type="number"
                  value={formData.port}
                  onChange={(e) => setFormData({ ...formData, port: parseInt(e.target.value) })}
                  placeholder="27017"
                  className="mt-1 font-mono text-sm"
                />
              </div>
            </div>

            <div>
              <Label>Database Name *</Label>
              <Input
                value={formData.database}
                onChange={(e) => setFormData({ ...formData, database: e.target.value })}
                placeholder="humatwin_production"
                className="mt-1 font-mono text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Username *</Label>
                <Input
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="db_user"
                  className="mt-1 font-mono text-sm"
                />
              </div>
              <div>
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="•••••••••"
                  className="mt-1 font-mono text-sm"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="ssl"
                checked={formData.ssl}
                onChange={(e) => setFormData({ ...formData, ssl: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="ssl" className="cursor-pointer">Enable SSL/TLS</Label>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleSaveConfig}
                disabled={!formData.host || !formData.database || !formData.username || !formData.password}
                data-testid="save-db-config"
              >
                Save Configuration
              </Button>
              <Button
                variant="outline"
                onClick={handleTestConnection}
                disabled={testing || !dbConfig?.configured}
                data-testid="test-db-connection"
              >
                {testing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Testing...
                  </>
                ) : (
                  'Test Connection'
                )}
              </Button>
            </div>

            {testResult && (
              <div className={`p-4 rounded-lg ${
                testResult.status === 'success'
                  ? 'bg-success/10 border border-success/20'
                  : 'bg-error/10 border border-error/20'
              }`}>
                <p className={`text-sm font-medium ${
                  testResult.status === 'success' ? 'text-success' : 'text-error'
                }`}>
                  {testResult.message}
                </p>
              </div>
            )}
          </Card>

          <Card className="p-6 rounded-lg bg-blue-50 border-blue-200">
            <h4 className="font-semibold text-gray-900 mb-2">Important Notes</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Credentials are encrypted at rest</li>
              <li>• Test connection before saving to verify settings</li>
              <li>• Ensure your database allows connections from HumaTwin IPs</li>
              <li>• Database must be MongoDB 4.0+ or CosmosDB with MongoDB API</li>
            </ul>
          </Card>
        </TabsContent>

        <TabsContent value="storage" className="space-y-4">
          <Card className="p-6 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-heading text-lg font-semibold text-gray-900">
                  Bring Your Own Storage (BYOS)
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Configure AWS S3, Azure Blob, or GCP Storage for file storage
                </p>
              </div>
              <Link to={`/enterprise/orgs/${orgId}/settings/infrastructure/storage`}>
                <Button>
                  Configure Storage
                </Button>
              </Link>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
